import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tabs, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ShopOutlined, ShoppingCartOutlined, CommentOutlined, DollarOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import api from '../services/api';
import { Store, PurchaseOrder, ReviewTask } from '../types';

const { Title } = Typography;
const { TabPane } = Tabs;

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [storeCount, setStoreCount] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [recentOrders, setRecentOrders] = useState<PurchaseOrder[]>([]);
  const [recentTasks, setRecentTasks] = useState<ReviewTask[]>([]);
  const [platformData, setPlatformData] = useState<{ name: string; value: number }[]>([]);
  const [statusData, setStatusData] = useState<{ name: string; value: number }[]>([]);
  const [amountTrend, setAmountTrend] = useState<[string, number][]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 獲取店鋪數據
        const stores = await api.stores.getAll() as Store[];
        setStoreCount(stores.length);

        // 獲取採購訂單數據
        const orders = await api.purchaseOrders.getAll() as PurchaseOrder[];
        setOrderCount(orders.length);
        setRecentOrders(orders.slice(0, 5));

        // 計算總金額
        const total = orders.reduce((sum: number, order: PurchaseOrder) => sum + order.total_amount, 0);
        setTotalAmount(total);

        // 獲取測評任務數據
        const tasks = await api.reviewTasks.getAll() as ReviewTask[];
        setTaskCount(tasks.length);
        setRecentTasks(tasks.slice(0, 5));

        // 處理平台分佈數據
        const platformMap: Record<string, number> = {};
        stores.forEach((store: Store) => {
          platformMap[store.platform] = (platformMap[store.platform] || 0) + 1;
        });
        const platformChartData = Object.keys(platformMap).map(platform => ({
          name: platform,
          value: platformMap[platform],
        }));
        setPlatformData(platformChartData);

        // 處理狀態分佈數據
        const statusMap: Record<string, number> = {};
        stores.forEach((store: Store) => {
          statusMap[store.status] = (statusMap[store.status] || 0) + 1;
        });
        const statusChartData = Object.keys(statusMap).map(status => ({
          name: status,
          value: statusMap[status],
        }));
        setStatusData(statusChartData);

        // 處理金額趨勢數據
        const dateMap: Record<string, number> = {};
        const today = new Date();
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          dateMap[dateStr] = 0;
        }

        orders.forEach((order: PurchaseOrder) => {
          // 將 Moment 對象轉換為字符串，或者直接使用字符串
          const dateStr = typeof order.purchase_date === 'string'
            ? order.purchase_date
            : order.purchase_date.format('YYYY-MM-DD');

          if (dateMap[dateStr] !== undefined) {
            dateMap[dateStr] += order.total_amount;
          }
        });

        const trendData = Object.keys(dateMap).map(date => [date, dateMap[date]] as [string, number]);
        setAmountTrend(trendData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 訂單表格列
  const orderColumns = [
    {
      title: t('purchaseOrders.orderId'),
      dataIndex: 'order_id',
      key: 'order_id',
    },
    {
      title: t('purchaseOrders.storeId'),
      dataIndex: 'store_id',
      key: 'store_id',
    },
    {
      title: t('purchaseOrders.platform'),
      dataIndex: 'platform',
      key: 'platform',
    },
    {
      title: t('purchaseOrders.purchaseDate'),
      dataIndex: 'purchase_date',
      key: 'purchase_date',
    },
    {
      title: t('purchaseOrders.totalAmount'),
      dataIndex: 'total_amount',
      key: 'total_amount',
    },
  ];

  // 任務表格列
  const taskColumns = [
    {
      title: t('reviewTasks.taskId'),
      dataIndex: 'task_id',
      key: 'task_id',
    },
    {
      title: t('reviewTasks.storeId'),
      dataIndex: 'store_id',
      key: 'store_id',
    },
    {
      title: t('reviewTasks.platform'),
      dataIndex: 'platform',
      key: 'platform',
    },
    {
      title: t('reviewTasks.date'),
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: t('reviewTasks.amount'),
      dataIndex: 'amount',
      key: 'amount',
    },
  ];

  // 平台分佈圖表選項
  const platformChartOption = {
    title: {
      text: t('dashboard.platformDistribution'),
      left: 'center',
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b} : {c} ({d}%)',
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [
      {
        name: t('stores.platform'),
        type: 'pie',
        radius: '50%',
        data: platformData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };

  // 狀態分佈圖表選項
  const statusChartOption = {
    title: {
      text: t('dashboard.statusDistribution'),
      left: 'center',
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b} : {c} ({d}%)',
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [
      {
        name: t('stores.status'),
        type: 'pie',
        radius: '50%',
        data: statusData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };

  // 金額趨勢圖表選項
  const amountTrendOption = {
    title: {
      text: t('dashboard.amountTrend'),
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
    },
    xAxis: {
      type: 'category',
      data: amountTrend.map(item => item[0]),
    },
    yAxis: {
      type: 'value',
    },
    series: [
      {
        name: t('purchaseOrders.totalAmount'),
        type: 'line',
        data: amountTrend.map(item => item[1]),
        markPoint: {
          data: [
            { type: 'max', name: 'Max' },
            { type: 'min', name: 'Min' },
          ],
        },
      },
    ],
  };

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Title level={2}>{t('dashboard.title')}</Title>

      {/* 統計卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('dashboard.storeCount')}
              value={storeCount}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('dashboard.orderCount')}
              value={orderCount}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('dashboard.taskCount')}
              value={taskCount}
              prefix={<CommentOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('dashboard.totalAmount')}
              value={totalAmount}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 圖表 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <ReactECharts option={platformChartOption} style={{ height: 300 }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <ReactECharts option={statusChartOption} style={{ height: 300 }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <ReactECharts option={amountTrendOption} style={{ height: 300 }} />
          </Card>
        </Col>
      </Row>

      {/* 最近數據 */}
      <Tabs defaultActiveKey="orders">
        <TabPane tab={t('dashboard.recentOrders')} key="orders">
          <Table
            columns={orderColumns}
            dataSource={recentOrders}
            rowKey="id"
            pagination={false}
          />
        </TabPane>
        <TabPane tab={t('dashboard.recentTasks')} key="tasks">
          <Table
            columns={taskColumns}
            dataSource={recentTasks}
            rowKey="id"
            pagination={false}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Dashboard;
